import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { dashboardApi, pdvRankingsApi } from '../lib/api';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Store,
  FileText,
  GraduationCap,
  ClipboardCheck,
  AlertTriangle,
  ArrowRight,
  Calendar,
  TrendingUp,
  UserPlus,
  Trophy,
  Medal,
  TrendingDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const StatCard = ({ icon: Icon, value, label, trend, color = 'primary' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="metric-card hover-card"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="metric-value">{value}</p>
        <p className="metric-label">{label}</p>
      </div>
      <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
        color === 'accent' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'
      }`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
    {trend && (
      <div className="flex items-center gap-1 mt-4 text-sm text-success">
        <TrendingUp className="w-4 h-4" />
        <span>{trend}</span>
      </div>
    )}
  </motion.div>
);

const ExpiryItem = ({ item, type }) => {
  const daysUntil = item.expiry_date 
    ? Math.ceil((new Date(item.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const getStatusColor = () => {
    if (!daysUntil) return 'neutral';
    if (daysUntil < 0) return 'error';
    if (daysUntil <= 7) return 'error';
    if (daysUntil <= 30) return 'warning';
    return 'success';
  };

  return (
    <div className="flex items-center justify-between p-3 border-b border-border last:border-0 data-row">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${
          getStatusColor() === 'error' ? 'bg-destructive' :
          getStatusColor() === 'warning' ? 'bg-warning' : 'bg-success'
        }`} />
        <div>
          <p className="font-medium text-sm">{item.name || item.course_name}</p>
          <p className="text-xs text-muted-foreground">
            {type === 'document' ? item.category : item.employee_name}
          </p>
        </div>
      </div>
      <Badge variant="outline" className={
        getStatusColor() === 'error' ? 'border-destructive text-destructive' :
        getStatusColor() === 'warning' ? 'border-warning text-warning' : ''
      }>
        {daysUntil !== null ? (
          daysUntil < 0 ? 'Scaduto' : `${daysUntil}g`
        ) : 'N/D'}
      </Badge>
    </div>
  );
};

const RecentCheckItem = ({ check }) => (
  <div className="flex items-center justify-between p-3 border-b border-border last:border-0 data-row">
    <div className="flex-1">
      <p className="font-medium text-sm">{check.template_name}</p>
      <p className="text-xs text-muted-foreground">
        {new Date(check.inspection_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
      </p>
    </div>
    <div className="flex items-center gap-3">
      <div className="text-right">
        <p className="text-sm font-semibold">{check.conformity_score}%</p>
        <Progress value={check.conformity_score} className="w-16 h-1.5" />
      </div>
      <Badge variant={check.status === 'completed' ? 'default' : 'destructive'}>
        {check.status === 'completed' ? 'OK' : 'Azione'}
      </Badge>
    </div>
  </div>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [haccpStats, setHaccpStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, rankingsRes, haccpRes] = await Promise.all([
          dashboardApi.getStats(),
          pdvRankingsApi.getRankings(),
          api.get('/haccp/dashboard').catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setRankings(rankingsRes.data || []);
        setHaccpStats(haccpRes.data);
      } catch (error) {
        toast.error('Errore nel caricamento delle statistiche');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const hasAlerts = (stats?.expiring_documents || 0) + (stats?.expiring_trainings || 0) + (stats?.action_required_checks || 0) > 0;
  const hasPendingRegistrations = user?.role === 'super_admin' && (stats?.pending_registrations || 0) > 0;
  const isDocente = user?.role === 'docente';
  // v110 — i laboratori NON gestiscono UL/Azienda/Formazione/Sicurezza:
  // nascondiamo per loro stats grid, scadenze documenti generiche, controlli
  // recenti, classifica UL e Azioni Rapide (Nuova UL, Doc, Formazione, Controllo).
  const isLab = user?.role === 'laboratorio_analisi';

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'eccellente': return 'bg-emerald-100 text-emerald-800';
      case 'buono': return 'bg-blue-100 text-blue-800';
      case 'sufficiente': return 'bg-amber-100 text-amber-800';
      case 'critico': return 'bg-rose-100 text-rose-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'eccellente': return 'Eccellente';
      case 'buono': return 'Buono';
      case 'sufficiente': return 'Sufficiente';
      case 'critico': return 'Critico';
      default: return 'Non valutato';
    }
  };

  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">
          {isDocente ? 'Area Docente - Gestione Materiale Didattico' : 'Panoramica della sicurezza delle Unità Locali'}
        </p>
      </div>

      {/* HACCP Status Tile — solo per ruoli che hanno il modulo HACCP attivo */}
      {haccpStats && haccpStats.totals?.pdv > 0 && (user?.has_haccp === true || user?.role === 'super_admin') && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border border-orange-200 rounded-lg p-4"
          data-testid="haccp-dashboard-tile"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white text-xl">🌡️</div>
              <div>
                <h3 className="font-bold text-lg">Stato Autocontrollo HACCP</h3>
                <p className="text-sm text-gray-600">Panoramica di adempimenti HACCP delle tue UL (ultimi 30 giorni)</p>
              </div>
            </div>
            <Link to="/haccp-temperature">
              <Button size="sm" variant="outline" className="border-orange-300">
                Vai ai moduli HACCP
              </Button>
            </Link>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white rounded-md p-3 border border-orange-100">
              <div className="text-2xl font-bold">{haccpStats.totals.pdv}</div>
              <div className="text-xs text-gray-500 uppercase">Unità Locali</div>
            </div>
            <Link to="/haccp" className="bg-white rounded-md p-3 border border-orange-100 hover:bg-orange-50 transition-colors">
              <div className="text-2xl font-bold text-emerald-600">{haccpStats.with_manual}<span className="text-base text-gray-400">/{haccpStats.totals.pdv}</span></div>
              <div className="text-xs text-gray-500 uppercase">Manuali HACCP</div>
            </Link>
            <Link to="/haccp-temperature" className="bg-white rounded-md p-3 border border-orange-100 hover:bg-orange-50 transition-colors">
              <div className={`text-2xl font-bold ${haccpStats.temperature_30d > 0 ? 'text-emerald-600' : 'text-amber-500'}`}>{haccpStats.temperature_30d}</div>
              <div className="text-xs text-gray-500 uppercase">Schede Temp. 30gg</div>
            </Link>
            <Link to="/haccp-cleaning" className="bg-white rounded-md p-3 border border-orange-100 hover:bg-orange-50 transition-colors">
              <div className={`text-2xl font-bold ${haccpStats.cleaning_30d > 0 ? 'text-emerald-600' : 'text-amber-500'}`}>{haccpStats.cleaning_30d}</div>
              <div className="text-xs text-gray-500 uppercase">Sanificazioni 30gg</div>
            </Link>
            <Link to="/haccp-inspection" className="bg-white rounded-md p-3 border border-orange-100 hover:bg-orange-50 transition-colors">
              <div className={`text-2xl font-bold ${haccpStats.inspection_30d > 0 ? 'text-emerald-600' : 'text-amber-500'}`}>{haccpStats.inspection_30d}</div>
              <div className="text-xs text-gray-500 uppercase">Ispezioni 30gg</div>
            </Link>
          </div>
        </motion.div>
      )}

      {/* Pending Registrations Alert */}
      {hasPendingRegistrations && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3"
        >
          <UserPlus className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-blue-800">Richieste di registrazione in attesa</p>
            <p className="text-sm text-blue-700">
              {stats?.pending_registrations} utenti attendono l'approvazione per accedere al sistema.
            </p>
          </div>
          <Link to="/registrations">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
              Gestisci
            </Button>
          </Link>
        </motion.div>
      )}

      {/* Alert Banner */}
      {hasAlerts && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert-banner warning mb-6 flex items-center gap-3"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Attenzione richiesta</p>
            <p className="text-sm">
              {stats?.expiring_documents || 0} documenti e {stats?.expiring_trainings || 0} formazioni in scadenza. 
              {stats?.action_required_checks > 0 && ` ${stats.action_required_checks} controlli richiedono intervento.`}
            </p>
          </div>
          <Link to="/notifications">
            <Button variant="outline" size="sm" className="border-amber-500 text-amber-800 hover:bg-amber-100">
              Vedi dettagli
            </Button>
          </Link>
        </motion.div>
      )}

      {/* Stats Grid - Hidden for Docente and Lab */}
      {!isDocente && !isLab && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Store}
            value={stats?.pdv_count || 0}
            label="Unità Locali"
          />
          <StatCard
            icon={FileText}
            value={stats?.documents_count || 0}
            label="Documenti"
          />
          <StatCard
            icon={GraduationCap}
            value={stats?.trainings_count || 0}
            label="Formazioni"
          />
          <StatCard
            icon={ClipboardCheck}
            value={stats?.safety_checks_count || 0}
            label="Controlli"
          />
        </div>
      )}

      {/* Docente Dashboard */}
      {isDocente && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className="hover-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                Materiale Didattico
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Gestisci i tuoi file PowerPoint e materiale per le lezioni
              </p>
              <Link to="/training-materials">
                <Button className="w-full">
                  Vai al Materiale Didattico
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
          <Card className="hover-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Fatture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Visualizza e gestisci le tue fatture
              </p>
              <Link to="/teacher-invoices">
                <Button variant="outline" className="w-full">
                  Vai alle Fatture
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bento Grid - Hidden for Docente and Lab */}
      {!isDocente && !isLab && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Expiring Items */}
          <Card className="lg:col-span-2 hover-card" data-testid="expiring-items-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                Scadenze Imminenti
              </CardTitle>
              <Link to="/documents">
                <Button variant="ghost" size="sm" className="text-xs">
                  Vedi tutti <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-0">
              {stats?.expiring_items?.length > 0 ? (
                <div className="divide-y divide-border">
                  {stats.expiring_items.map((item, index) => (
                    <ExpiryItem 
                      key={index} 
                      item={item} 
                      type={item.category ? 'document' : 'training'} 
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-state py-8">
                  <Calendar className="empty-state-icon" />
                  <p className="empty-state-title">Nessuna scadenza imminente</p>
                  <p className="empty-state-description">Tutti i documenti e le formazioni sono in regola</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Checks */}
          <Card className="row-span-2 hover-card" data-testid="recent-checks-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-muted-foreground" />
                Controlli Recenti
              </CardTitle>
              <Link to="/safety-checks">
                <Button variant="ghost" size="sm" className="text-xs">
                  Vedi tutti <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-0">
              {stats?.recent_checks?.length > 0 ? (
                <div className="divide-y divide-border">
                  {stats.recent_checks.map((check) => (
                    <RecentCheckItem key={check.id} check={check} />
                  ))}
                </div>
              ) : (
                <div className="empty-state py-8">
                <ClipboardCheck className="empty-state-icon" />
                <p className="empty-state-title">Nessun controllo</p>
                <p className="empty-state-description">Inizia effettuando un controllo sicurezza</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions — nascoste per docente_online (v1.1.1) */}
        {user?.role !== 'docente_online' && (
        <Card className="lg:col-span-2 hover-card" data-testid="quick-actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link to="/pdv" className="block">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                  <Store className="w-5 h-5" />
                  <span className="text-xs">Nuova UL</span>
                </Button>
              </Link>
              <Link to="/documents" className="block">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                  <FileText className="w-5 h-5" />
                  <span className="text-xs">Aggiungi Doc</span>
                </Button>
              </Link>
              <Link to="/training" className="block">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                  <GraduationCap className="w-5 h-5" />
                  <span className="text-xs">Formazione</span>
                </Button>
              </Link>
              <Link to="/safety-checks" className="block">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2 border-accent text-accent hover:bg-accent/10">
                  <ClipboardCheck className="w-5 h-5" />
                  <span className="text-xs">Controllo</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        )}

        {/* PDV Rankings - Classifica Sicurezza */}
        {rankings.length > 0 && (
          <Card className="lg:col-span-3 hover-card" data-testid="pdv-rankings-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Classifica UL - Conformità Sicurezza
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {rankings.slice(0, 10).map((pdv, idx) => (
                  <div 
                    key={pdv.pdv_id} 
                    className={`flex items-center gap-4 p-3 rounded-sm border ${
                      idx === 0 ? 'border-amber-300 bg-amber-50' : 
                      idx === 1 ? 'border-gray-300 bg-gray-50' : 
                      idx === 2 ? 'border-orange-200 bg-orange-50' : 
                      'border-border'
                    }`}
                  >
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      idx === 0 ? 'bg-amber-500 text-white' : 
                      idx === 1 ? 'bg-gray-400 text-white' : 
                      idx === 2 ? 'bg-orange-400 text-white' : 
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {idx < 3 ? <Medal className="w-4 h-4" /> : pdv.rank}
                    </div>
                    
                    {/* PDV Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{pdv.pdv_name}</p>
                      <p className="text-xs text-muted-foreground">{pdv.city} • {pdv.total_checks} controlli</p>
                    </div>
                    
                    {/* Score Bar */}
                    <div className="w-32 hidden md:block">
                      <Progress value={pdv.score} className="h-2" />
                    </div>
                    
                    {/* Score */}
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        pdv.score >= 90 ? 'text-emerald-600' : 
                        pdv.score >= 70 ? 'text-blue-600' : 
                        pdv.score >= 50 ? 'text-amber-600' : 
                        pdv.score > 0 ? 'text-rose-600' : 'text-gray-400'
                      }`}>
                        {pdv.score}%
                      </div>
                      <Badge className={`text-xs ${getStatusColor(pdv.status)}`}>
                        {getStatusLabel(pdv.status)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              
              {rankings.length > 10 && (
                <div className="mt-4 text-center">
                  <Link to="/safety-checks">
                    <Button variant="outline" size="sm">
                      Vedi tutte le {rankings.length} UL
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        </div>
      )}
    </div>
  );
}
